import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { PageSEO } from '../components/SEO';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Brain, Target, BookOpen, MessageCircle, BarChart3, Sparkles, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

export default function LandingPage() {
  const { t } = useTranslation();

  const features = [
    {
      icon: MessageCircle,
      title: t('landing.features.ai_coach.title'),
      description: t('landing.features.ai_coach.description')
    },
    {
      icon: Brain,
      title: t('landing.features.meditations.title'),
      description: t('landing.features.meditations.description')
    },
    {
      icon: Target,
      title: t('landing.features.goals.title'),
      description: t('landing.features.goals.description')
    },
    {
      icon: BookOpen,
      title: t('landing.features.journal.title'),
      description: t('landing.features.journal.description')
    },
    {
      icon: Sparkles,
      title: t('affirmations.title'),
      description: t('affirmations.subtitle')
    },
    {
      icon: BarChart3,
      title: t('analytics.title'),
      description: t('analytics.subtitle')
    }
  ];

  const categories = [
    {
      title: t('landing.categories.athlete'),
      image: "https://images.unsplash.com/photo-1623020687089-10f7d6f87026?w=400&q=80",
      description: t('landing.features.ai_coach.description')
    },
    {
      title: t('landing.categories.golfer'),
      image: "https://images.pexels.com/photos/114972/pexels-photo-114972.jpeg?w=400&q=80",
      description: t('landing.features.meditations.description')
    },
    {
      title: t('landing.categories.business'),
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&q=80",
      description: t('landing.features.goals.description')
    }
  ];
  return (
    <div className="min-h-screen bg-background">
      <PageSEO.Landing />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1728908053208-b7403ce54b6b?w=1920&q=80')",
            filter: "brightness(0.3)"
          }}
        />
        
        {/* Hero glow */}
        <div className="absolute inset-0 hero-glow" />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-24">
          <nav className="absolute top-0 left-0 right-0 py-6 px-6 md:px-12 lg:px-24 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-8 w-8 text-primary" />
              <span className="text-2xl font-serif font-bold text-foreground">MindForge</span>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Link to="/auth">
                <Button variant="ghost" className="text-foreground hover:bg-white/10" data-testid="nav-login-btn">
                  {t('auth.sign_in')}
                </Button>
              </Link>
            </div>
          </nav>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl pt-24"
          >
            <p className="font-mono text-xs tracking-widest uppercase text-primary/80 mb-4">
              Mental Performance Training
            </p>
            <h1 className="font-serif font-bold text-5xl md:text-7xl tracking-tight leading-none text-foreground mb-6">
              {t('landing.hero.title').split(' ').slice(0, 2).join(' ')}
              <span className="gradient-text block">{t('landing.hero.title').split(' ').slice(2).join(' ')}</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/subscription">
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-semibold shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all hover:scale-105"
                  data-testid="hero-cta-btn"
                >
                  {t('landing.hero.cta')}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-foreground hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                data-testid="learn-more-btn"
                onClick={() => document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t('landing.hero.secondary_cta')}
              </Button>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div 
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 bg-primary rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features-section" className="py-24 px-6 md:px-12 lg:px-24 bg-background">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-xs tracking-widest uppercase text-primary/80 mb-4">
              {t('landing.features.title')}
            </p>
            <h2 className="font-serif font-semibold text-3xl md:text-5xl tracking-tight text-foreground">
              {t('landing.features.title')}
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Link to="/subscription" key={feature.title}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative overflow-hidden bg-card/50 hover:bg-card/80 border border-white/5 hover:border-primary/30 rounded-2xl p-6 transition-all duration-500 cursor-pointer h-full"
                >
                <div className="card-shine absolute inset-0 pointer-events-none" />
                <feature.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-serif font-semibold text-xl text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-card/30">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-xs tracking-widest uppercase text-primary/80 mb-4">
              {t('landing.categories.title')}
            </p>
            <h2 className="font-serif font-semibold text-3xl md:text-5xl tracking-tight text-foreground">
              {t('landing.categories.title')}
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div className="aspect-[4/5] relative">
                  <img 
                    src={category.image} 
                    alt={category.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-serif font-bold text-2xl text-foreground mb-2">{category.title}</h3>
                    <p className="text-muted-foreground text-sm">{category.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-background relative overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-12 md:p-16 text-center"
          >
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-foreground mb-6">
              {t('landing.cta_section.title')}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              {t('landing.cta_section.subtitle')}
            </p>
            <Link to="/auth">
              <Button 
                size="lg" 
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-semibold animate-pulse-glow"
                data-testid="bottom-cta-btn"
              >
                {t('landing.cta_section.button')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 lg:px-24 border-t border-border">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            <span className="font-serif font-bold text-foreground">MindForge</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2024 MindForge. Forge your mental edge.
          </p>
        </div>
      </footer>
    </div>
  );
}
